import {PriorityConfig} from '@aboutyou/data-fetcher';
import {Grid} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {TilesByPage} from '../../../features/ProductStream/TilesByPage';
import {StreamPaddingCSS} from '../../../features/ProductStream/helpers';
import {
  LoadMoreComponentDisplayConfig,
  ProductStreamTokenPagination,
} from '../../../features/ProductStreamPagination/ProductStreamTokenPagination';
import {useShallowRenderingState} from '../../../features/ProductTile/utils';
import {useTokenBasedStreamPages} from '../../../features/TokenBasedStreamPages/useTokenBasedStreamPages';
import {usePreventProductTilesSwipeAnimation} from '../../CouponLandingPage/ProductStream/usePreventProductTilesSwipeAnimation';
import {useInitialStreamEmpty} from '../hooks/useInitialStreamEmpty';
import {useSearchPageStreamSection} from '../hooks/useSearchPageBulkSection';
import {useSearchResultsStream} from '../hooks/useSearchResultsStream';
import {
  SearchResultsStreamTileList,
  SearchResultsStreamTileListProps,
} from './SearchResultsStreamTileList';
import {SearchStreamEmpty} from './SearchStreamEmpty';

const HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY =
  'search_results_stream_last_visible_items';

interface SearchResultsStreamProps
  extends SearchResultsStreamTileListProps,
    PriorityConfig {}

export const SearchResultsStream: React.FC<SearchResultsStreamProps> = ({
  gridColumns,
  onClickAddCta,
  productTileGalleryMode,
  enableImageGalleryAnimation,
  ProductTileComponent,
}) => {
  const {pages, isNextPageReady, mountNextPage} = useTokenBasedStreamPages({
    uniqueKey: 'search-results',
    request: useSearchPageStreamSection,
    nextRequest: useSearchResultsStream,
  });

  const shallowRenderingState = useShallowRenderingState({
    uniqueKey: HISTORY_STATE_LAST_VISIBLE_ITEMS_KEY,
  });

  const preventProductTilesSwipeAnimation =
    usePreventProductTilesSwipeAnimation();

  const isInitialRequestEmpty = useInitialStreamEmpty(
    useSearchPageStreamSection,
  );

  return (
    <section>
      <StyledGrid
        columns={gridColumns}
        enableEnhancedTileRendering
        uniqueKey="searchResultsStream"
      >
        {pages.map((page, i) => (
          <TilesByPage
            key={i}
            page={page}
            StreamTileList={SearchResultsStreamTileList}
            gridColumns={gridColumns}
            shallowRenderingState={shallowRenderingState}
            onClickAddCta={onClickAddCta}
            productTileGalleryMode={productTileGalleryMode}
            enableImageGalleryAnimation={
              enableImageGalleryAnimation && !preventProductTilesSwipeAnimation
            }
            ProductTileComponent={ProductTileComponent}
          />
        ))}
      </StyledGrid>
      {isNextPageReady && (
        <ProductStreamTokenPagination
          mountNextPage={mountNextPage}
          loadMoreComponentDisplayConfig={loadMoreComponentDisplayConfig}
        />
      )}
      {isInitialRequestEmpty && <SearchStreamEmpty />}
    </section>
  );
};

const StyledGrid = styled(Grid)`
  ${StreamPaddingCSS}
`;

const loadMoreComponentDisplayConfig: LoadMoreComponentDisplayConfig = {
  tilesBeforeFirstDisplay: 1000,
  tilesBeforeConsecutivelyDisplay: 1000,
};
